import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  Home, Building2, TrendingUp, Receipt, Wallet, Clock, CheckCircle, XCircle, Percent, ExternalLink, Store,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RestaurantPortalPage = () => {
  const { user, token, loading: authLoading, logout } = useAuth();
  const { language } = useLanguage();

  const [me, setMe] = useState(null);
  const [restaurantId, setRestaurantId] = useState('');
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payouts, setPayouts] = useState({ pending: [], history: [] });
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const meRes = await fetch(`${API_URL}/api/restaurant-portal/me`, { headers });
      if (!meRes.ok) {
        setLoading(false);
        return;
      }
      const meData = await meRes.json();
      setMe(meData);
      const firstRid = meData.restaurants?.[0]?.restaurant_id || '';
      const rid = restaurantId || firstRid;
      if (!restaurantId) setRestaurantId(firstRid);

      const qs = rid ? `restaurant_id=${rid}&` : '';
      const [s, o, p] = await Promise.all([
        fetch(`${API_URL}/api/restaurant-portal/summary?${qs}days=${period}`, { headers }),
        fetch(`${API_URL}/api/restaurant-portal/orders?${qs}limit=100`, { headers }),
        fetch(`${API_URL}/api/restaurant-portal/payouts?${qs}`, { headers }),
      ]);
      if (s.ok) setSummary(await s.json());
      if (o.ok) setOrders(await o.json());
      if (p.ok) setPayouts(await p.json());
    } catch (e) {
      console.error('Portal fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [token, restaurantId, period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF6B35] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login?redirect=/restaurant-portal" replace />;
  if (!['admin', 'super_admin', 'restaurant_manager'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  const statusColor = (s) => ({
    received: 'bg-yellow-100 text-yellow-800',
    preparing: 'bg-blue-100 text-blue-800',
    ready: 'bg-purple-100 text-purple-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    pending_payment: 'bg-gray-100 text-gray-700',
  })[s] || 'bg-gray-100 text-gray-700';

  const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="restaurant-portal">
      {/* Header */}
      <div className="bg-[#004E64] text-white py-5 px-6">
        <div className="container mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/"><Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full"><Home size={18} /></Button></Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Building2 size={20} /> {language === 'pt' ? 'Portal do Restaurante' : 'Restaurant Portal'}
              </h1>
              <p className="text-xs opacity-80">{me?.user?.name} · {me?.user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/pos" data-testid="portal-pos-link">
              <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
                <Store size={14} className="mr-1" />
                {language === 'pt' ? 'POS — Receber Pedidos' : 'POS — Receive Orders'}
              </Button>
            </Link>
            {me?.restaurants?.length > 1 && (
              <Select value={restaurantId} onValueChange={setRestaurantId}>
                <SelectTrigger className="w-56 bg-white/10 border-white/20 text-white rounded-full" data-testid="portal-restaurant-select">
                  <SelectValue placeholder={language === 'pt' ? 'Escolher restaurante' : 'Choose restaurant'} />
                </SelectTrigger>
                <SelectContent>
                  {me.restaurants.map((r) => <SelectItem key={r.restaurant_id} value={r.restaurant_id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
              <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white rounded-full" data-testid="portal-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">{language === 'pt' ? 'Últimos 7d' : 'Last 7d'}</SelectItem>
                <SelectItem value="30">{language === 'pt' ? 'Últimos 30d' : 'Last 30d'}</SelectItem>
                <SelectItem value="90">{language === 'pt' ? 'Últimos 90d' : 'Last 90d'}</SelectItem>
                <SelectItem value="365">{language === 'pt' ? 'Último ano' : 'Last year'}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => logout()} variant="outline" className="text-white border-white/30 hover:bg-white/10 rounded-full" data-testid="portal-logout">
              {language === 'pt' ? 'Sair' : 'Sign out'}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {loading || !summary ? (
            Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)
          ) : (
            <>
              <Card className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-0">
                <CardContent className="p-4">
                  <p className="text-xs text-[#9FA2B4] flex items-center gap-1"><TrendingUp size={12} /> {language === 'pt' ? 'Receita bruta' : 'Gross revenue'}</p>
                  <p className="text-2xl font-bold text-[#004E64] mt-1" data-testid="portal-gross">{fmt(summary.gross)}</p>
                  <p className="text-xs text-[#9FA2B4] mt-1">{summary.delivered} {language === 'pt' ? 'entregues' : 'delivered'}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl bg-gradient-to-br from-orange-50 to-red-50 border-0">
                <CardContent className="p-4">
                  <p className="text-xs text-[#9FA2B4] flex items-center gap-1"><Percent size={12} /> {language === 'pt' ? `Comissão WEGOO (${Math.round((summary.commission_rate || 0.15) * 100)}%)` : `WEGOO Fee (${Math.round((summary.commission_rate || 0.15) * 100)}%)`}</p>
                  <p className="text-2xl font-bold text-[#FF6B35] mt-1" data-testid="portal-commission">−{fmt(summary.commission)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border-0">
                <CardContent className="p-4">
                  <p className="text-xs text-[#9FA2B4] flex items-center gap-1"><Wallet size={12} /> {language === 'pt' ? 'Líquido a receber' : 'Net to receive'}</p>
                  <p className="text-2xl font-bold text-green-700 mt-1" data-testid="portal-net">{fmt(summary.net)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border-0">
                <CardContent className="p-4">
                  <p className="text-xs text-[#9FA2B4] flex items-center gap-1"><XCircle size={12} /> {language === 'pt' ? 'Cancelados' : 'Cancelled'}</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{summary.cancelled}</p>
                  <p className="text-xs text-[#9FA2B4] mt-1">{summary.orders_count} {language === 'pt' ? 'pedidos no total' : 'orders total'}</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="bg-[#F4F1EA] rounded-full">
            <TabsTrigger value="orders" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white" data-testid="portal-tab-orders">
              <Receipt size={14} className="mr-1" /> {language === 'pt' ? 'Pedidos' : 'Orders'}
            </TabsTrigger>
            <TabsTrigger value="payouts-pending" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white" data-testid="portal-tab-pending">
              <Clock size={14} className="mr-1" /> {language === 'pt' ? 'A receber' : 'Pending payouts'}
            </TabsTrigger>
            <TabsTrigger value="payouts-history" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white" data-testid="portal-tab-history">
              <CheckCircle size={14} className="mr-1" /> {language === 'pt' ? 'Pagos' : 'Paid'}
            </TabsTrigger>
          </TabsList>

          {/* Orders */}
          <TabsContent value="orders" className="mt-4">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="text-base">{language === 'pt' ? 'Pedidos recentes' : 'Recent orders'}</CardTitle></CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-sm text-[#9FA2B4] py-6 text-center">{language === 'pt' ? 'Sem pedidos no período.' : 'No orders in the period.'}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="portal-orders-table">
                      <thead className="text-left text-[#9FA2B4] border-b">
                        <tr>
                          <th className="py-2 pr-2">#</th>
                          <th className="py-2 pr-2">{language === 'pt' ? 'Data' : 'Date'}</th>
                          <th className="py-2 pr-2">{language === 'pt' ? 'Cliente' : 'Customer'}</th>
                          <th className="py-2 pr-2">{language === 'pt' ? 'Estado' : 'Status'}</th>
                          <th className="py-2 pr-2 text-right">{language === 'pt' ? 'Subtotal' : 'Subtotal'}</th>
                          <th className="py-2 pr-2 text-right">{language === 'pt' ? 'Comissão' : 'Commission'}</th>
                          <th className="py-2 pr-2 text-right">{language === 'pt' ? 'Líquido' : 'Net'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.slice(0, 50).map((o) => (
                          <tr key={o.order_id} className="border-b last:border-0 hover:bg-[#FAFAF7]" data-testid={`portal-order-${o.order_id}`}>
                            <td className="py-2 pr-2 font-mono text-xs">{o.order_id?.slice(-8)}</td>
                            <td className="py-2 pr-2 text-xs">{new Date(o.created_at).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}</td>
                            <td className="py-2 pr-2 truncate max-w-[8rem]">{o.customer_name}</td>
                            <td className="py-2 pr-2"><Badge className={statusColor(o.status)}>{o.status}</Badge></td>
                            <td className="py-2 pr-2 text-right">{fmt(o.subtotal)}</td>
                            <td className="py-2 pr-2 text-right text-[#FF6B35]">{o._commission ? `−${fmt(o._commission)}` : '—'}</td>
                            <td className="py-2 pr-2 text-right font-bold text-green-700">{o._net ? fmt(o._net) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending payouts */}
          <TabsContent value="payouts-pending" className="mt-4">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="text-base">{language === 'pt' ? 'Pagamentos pendentes' : 'Pending payouts'}</CardTitle></CardHeader>
              <CardContent>
                {payouts.pending.length === 0 ? (
                  <p className="text-sm text-[#9FA2B4] py-6 text-center">{language === 'pt' ? 'Sem pagamentos pendentes.' : 'No pending payouts.'}</p>
                ) : (
                  <div className="space-y-2">
                    {payouts.pending.map((p) => (
                      <div key={p.payout_id} className="border border-[#F4F1EA] rounded-xl p-3 flex items-center justify-between" data-testid={`portal-pending-${p.payout_id}`}>
                        <div>
                          <p className="font-medium text-[#004E64]">{p.restaurant_name}</p>
                          <p className="text-xs text-[#9FA2B4]">
                            {p.orders_count} {language === 'pt' ? 'pedidos' : 'orders'} · {language === 'pt' ? 'Bruto' : 'Gross'} {fmt(p.gross)} · {language === 'pt' ? 'Comissão' : 'Fee'} −{fmt(p.commission)}
                          </p>
                          <p className="text-xs text-[#9FA2B4]">{new Date(p.period_start).toLocaleDateString('pt-PT')} – {new Date(p.period_end).toLocaleDateString('pt-PT')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-700">{fmt(p.amount)}</p>
                          <Badge className="bg-yellow-100 text-yellow-700">{language === 'pt' ? 'Pendente' : 'Pending'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History */}
          <TabsContent value="payouts-history" className="mt-4">
            <Card className="rounded-2xl">
              <CardHeader className="pb-2"><CardTitle className="text-base">{language === 'pt' ? 'Histórico de pagamentos' : 'Payout history'}</CardTitle></CardHeader>
              <CardContent>
                {payouts.history.length === 0 ? (
                  <p className="text-sm text-[#9FA2B4] py-6 text-center">{language === 'pt' ? 'Sem histórico ainda.' : 'No history yet.'}</p>
                ) : (
                  <div className="space-y-2">
                    {payouts.history.map((p) => (
                      <div key={p.payout_id} className="border border-[#F4F1EA] rounded-xl p-3 flex items-center justify-between" data-testid={`portal-history-${p.payout_id}`}>
                        <div>
                          <p className="font-medium text-[#004E64]">{fmt(p.amount)} <span className="text-xs text-[#9FA2B4] font-normal">· {p.orders_count} {language === 'pt' ? 'pedidos' : 'orders'}</span></p>
                          <p className="text-xs text-[#9FA2B4]">{new Date(p.processed_at || p.created_at).toLocaleString('pt-PT')}</p>
                          {p.transfer_reference && <p className="text-xs text-[#9FA2B4]"><ExternalLink size={10} className="inline mr-1" />Ref: {p.transfer_reference}</p>}
                        </div>
                        <Badge className={p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>{p.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default RestaurantPortalPage;
